import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
  useCallback,
  CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import { IUseUserMenuArgs, IUseUserMenuReturn } from "@model/NavBar.model";

type UserMenuOpts = {
  placement?: "left" | "right";
  matchTriggerWidth?: boolean;
  closeOnResize?: boolean;
  closeOnScroll?: boolean;
  idPrefix?: string;
};

export const useUserMenu = (
  { firstName = "", lastName = "" }: IUseUserMenuArgs = {},
  {
    placement = "right",
    matchTriggerWidth = false,
    closeOnResize = false,
    closeOnScroll = false,
    idPrefix = "user",
  }: UserMenuOpts = {}
): IUseUserMenuReturn => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null!);
  const menuRef = useRef<HTMLDivElement>(null!);
  const navigate = useNavigate();

  const rid = useId();
  const triggerId = `${idPrefix}-menu-trigger-${rid}`;
  const dropdownId = `${idPrefix}-dropdown-${rid}`;

  const fullName = useMemo(
    () => [firstName, lastName].filter(Boolean).join(" ").trim(),
    [firstName, lastName]
  );

  const capitalizeString = (name: string): string => {
    return name?.[0]?.toUpperCase() ?? "";
  };

  const initials = useMemo(() => {
    const capitalizedFirstName = capitalizeString(firstName);
    const capitalizedLastName = capitalizeString(lastName);
    return (
      capitalizedFirstName + capitalizedLastName ||
      capitalizedFirstName ||
      capitalizedLastName ||
      "U"
    );
  }, [firstName, lastName]);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        btnRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  // Optional close on resize/scroll
  useEffect(() => {
    if (!open) return;
    const onResize = () => closeOnResize && close();
    const onScroll = () => closeOnScroll && close();
    if (closeOnResize) window.addEventListener("resize", onResize);
    if (closeOnScroll) window.addEventListener("scroll", onScroll, true);
    return () => {
      if (closeOnResize) window.removeEventListener("resize", onResize);
      if (closeOnScroll) window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, closeOnResize, closeOnScroll, close]);

  const panelStyle: CSSProperties | undefined = useMemo(() => {
    if (!open) return undefined;
    const trigger = btnRef.current;
    if (!trigger) return undefined;
    return {
      top: "calc(100% + .35rem)",
      left: placement === "left" ? 0 : undefined,
      right: placement === "right" ? 0 : undefined,
      minWidth: matchTriggerWidth ? trigger.offsetWidth : undefined,
    };
  }, [open, placement, matchTriggerWidth]);

  // Close menu, then run a callback (navigate, logout, etc.)
  const onItemClick = useCallback(
    (cb?: () => void) => (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setOpen(false);
      cb?.();
    },
    []
  );

  // Use inside onClick: onClick={goTo("/path")}
  const goTo = useCallback(
    (path: string) =>
      onItemClick(() => {
        navigate(path);
      }),
    [navigate, onItemClick]
  );

  return {
    open,
    toggle,
    close,
    triggerId,
    dropdownId,
    fullName,
    initials,
    goTo,
    onItemClick,
    btnRef,
    menuRef,
    panelStyle,
  };
};
